import type { CompiledWorldChunk, MeshKind } from '../../chunk-schema/src/index.ts'

/** The complete render allowlist for the scaled-back alpha. */
export const RUNTIME_STRUCTURE_MESH_KINDS = [
  'bridge-deck',
  'bridge-barrier',
  'bridge-support',
  'tunnel-shell',
  'tunnel-portal',
  'retaining-wall',
] as const satisfies readonly MeshKind[]

export const RUNTIME_MESH_KINDS = [
  'terrain',
  'road',
  'junction',
  ...RUNTIME_STRUCTURE_MESH_KINDS,
] as const satisfies readonly MeshKind[]
export type RuntimeMeshKind = (typeof RUNTIME_MESH_KINDS)[number]

const runtimeMeshKinds = new Set<MeshKind>(RUNTIME_MESH_KINDS)
const runtimeStructureMeshKinds = new Set<MeshKind>(RUNTIME_STRUCTURE_MESH_KINDS)

export function isRuntimeMeshKind(kind: MeshKind): kind is RuntimeMeshKind {
  return runtimeMeshKinds.has(kind)
}

export function isRuntimeStructureMeshKind(kind: MeshKind): kind is (typeof RUNTIME_STRUCTURE_MESH_KINDS)[number] {
  return runtimeStructureMeshKinds.has(kind)
}

/**
 * Keep the canonical cached chunk intact in the worker, but send the main
 * thread only the terrain, road and vertical-structure authority used at runtime.
 */
export function createRuntimeChunkView(chunk: CompiledWorldChunk): CompiledWorldChunk {
  const meshes = chunk.meshes.filter((mesh) => isRuntimeMeshKind(mesh.kind))
  const structureColliderIds = new Set(
    meshes
      .filter((mesh) => isRuntimeStructureMeshKind(mesh.kind))
      .map((mesh) => `collision:${mesh.id}`),
  )
  return {
    ...chunk,
    meshes,
    // Road height authority comes directly from the transferred render meshes;
    // terrain remains a packed heightfield. Only lateral structure collision
    // needs a separate triangle collider across the worker boundary.
    colliders: chunk.colliders.filter((collider) => (
      collider.kind === 'trimesh'
      && collider.material !== 'asphalt'
      && structureColliderIds.has(collider.id)
    )),
  }
}
